import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { api, FalhaApi, type UsuarioSessao } from "../api";

interface ContextoSessao {
  usuario: UsuarioSessao | null;
  carregando: boolean;
  entrar: (email: string, senha: string) => Promise<void>;
  sair: () => Promise<void>;
}

const Contexto = createContext<ContextoSessao | null>(null);

// Cache só de dados de exibição (id/nome/papel/organização) — não é o token de sessão, que fica
// no cookie httpOnly de qualquer forma. Existe pra sobreviver ao remount da Fase 6: fechar/reabrir
// o PWA sem sinal precisa continuar mostrando o usuário logado, não chutar pra tela de login.
const CHAVE_CACHE_USUARIO = "vistoria-usuario-cache";

function lerUsuarioCache(): UsuarioSessao | null {
  try {
    const bruto = localStorage.getItem(CHAVE_CACHE_USUARIO);
    return bruto ? (JSON.parse(bruto) as UsuarioSessao) : null;
  } catch {
    return null;
  }
}

function gravarUsuarioCache(usuario: UsuarioSessao | null) {
  try {
    if (usuario) localStorage.setItem(CHAVE_CACHE_USUARIO, JSON.stringify(usuario));
    else localStorage.removeItem(CHAVE_CACHE_USUARIO);
  } catch {
    // localStorage pode falhar (modo privado, quota) — cache é só uma otimização, não crítico.
  }
}

export function ProvedorSessao({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<UsuarioSessao | null>(lerUsuarioCache);
  // Só bloqueia em "Carregando…" quando não há usuário em cache — pra quem já tinha aberto o app
  // antes, mostra a tela na hora com o que já se sabia, e confirma em segundo plano. Sem isso,
  // reabrir o PWA sem sinal (fetch pode demorar dezenas de segundos pra desistir sozinho num
  // celular realmente sem sinal, diferente de uma conexão recusada) prenderia até quem já estava
  // logado numa tela de carregamento por todo esse tempo — justamente o cenário que a Fase 6
  // existe pra cobrir.
  const [carregando, setCarregando] = useState(() => lerUsuarioCache() === null);

  useEffect(() => {
    let cancelado = false;
    api
      .eu()
      .then(({ usuario }) => {
        if (!cancelado) {
          setUsuario(usuario);
          gravarUsuarioCache(usuario);
        }
      })
      .catch((e) => {
        if (cancelado) return;
        // 401 com corpo JSON válido é o servidor de fato dizendo "sem sessão" (expirou, foi
        // revogada, nunca existiu) — nesse caso limpa mesmo. Qualquer outra falha (rede fora do
        // ar, proxy sem resposta) não prova que a sessão é inválida, só que não deu pra checar
        // agora — mantém o que já tinha em cache em vez de deslogar um usuário offline que
        // continua com um cookie de sessão perfeitamente válido.
        const semSessaoDeVerdade = e instanceof FalhaApi && e.status === 401 && !e.corpoInvalido;
        if (semSessaoDeVerdade) {
          setUsuario(null);
          gravarUsuarioCache(null);
        }
      })
      .finally(() => {
        if (!cancelado) setCarregando(false);
      });
    return () => {
      cancelado = true;
    };
  }, []);

  const entrar = useCallback(async (email: string, senha: string) => {
    const { usuario } = await api.login(email, senha);
    setUsuario(usuario);
    gravarUsuarioCache(usuario);
  }, []);

  const sair = useCallback(async () => {
    await api.logout().catch(() => undefined);
    setUsuario(null);
    gravarUsuarioCache(null);
  }, []);

  return (
    <Contexto.Provider value={{ usuario, carregando, entrar, sair }}>
      {children}
    </Contexto.Provider>
  );
}

export function useSessao(): ContextoSessao {
  const contexto = useContext(Contexto);
  if (!contexto) throw new Error("useSessao precisa estar dentro de ProvedorSessao");
  return contexto;
}
